/* eslint-disable jsdoc/check-param-names */
import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import Select from "react-select";
import { useSB } from "../../../../contexts/SupabaseContext";
import { useAuth } from "../../../../contexts/AuthContext";

/**
 * Modal component for changing user type (admin only)
 *
 * @param {boolean} open - Whether the modal is open
 * @param {Function} onClose - Function to close the modal
 * @param {object} dealer - The dealer object
 * @param {Function} onRefresh - Function to refresh dealer details after update
 */
export default function ChangeUserTypeModal({
  open,
  onClose,
  dealer,
  onRefresh,
}) {
  const { GetTableWhere, updateRow } = useSB();
  const { currentUser } = useAuth();
  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingTypes, setFetchingTypes] = useState(false);
  const [error, setError] = useState("");

  // Fetch user types when modal opens
  useEffect(() => {
    let isMounted = true;

    if (open) {
      setFetchingTypes(true);
      setError("");

      GetTableWhere("refs_user_type")
        .then(data => {
          if (!isMounted) return; // Don't update state if unmounted

          const userTypeOptions = data.map(item => ({
            label: item.type,
            value: item.id,
            data: item,
          }));

          setUserTypes(userTypeOptions);

          // Set current user type as default
          const currentType = userTypeOptions.find(
            type => type.value === dealer.refs_type_id
          );
          setSelectedUserType(currentType || userTypeOptions[0]);
        })
        .catch(err => {
          if (!isMounted) return; // Don't update state if unmounted

          console.error("Error fetching user types:", err);
          setError("Failed to load user types");
        })
        .finally(() => {
          if (isMounted) {
            setFetchingTypes(false);
          }
        });
    }

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [open, dealer.refs_type_id, GetTableWhere]);

  const handleSave = async () => {
    if (!selectedUserType) {
      setError("Please select a user type");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await updateRow("users_master", dealer.id, {
        refs_type_id: selectedUserType.value,
        last_updated_by_auth_id: currentUser.auth_id,
      });

      // Check if component is still mounted before updating state
      if (result.error) {
        setError(result.error.message);
        setLoading(false);
      } else {
        // Only call callbacks if successful - these handle their own state
        onRefresh();
        onClose();
        // Don't set loading to false here since onClose will unmount the component
      }
    } catch (err) {
      console.error("Error updating user type:", err);
      setError("Failed to update user type");
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError("");
      setSelectedUserType(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 },
      }}
    >
      <DialogTitle>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Change User Type
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        {fetchingTypes ? (
          <CircularProgress size={24} />
        ) : (
          <Select
            value={selectedUserType}
            onChange={setSelectedUserType}
            options={userTypes}
            placeholder="Select User Type"
            isDisabled={loading}
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }),
            }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || fetchingTypes || !selectedUserType}
          startIcon={loading ? <CircularProgress size={16} /> : null}
          sx={{ borderRadius: 2 }}
        >
          {loading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
